/**
 * Every date this business reads is dd/mm/yyyy.
 *
 * Why this exists
 * ---------------
 * Dates were rendered with `toLocaleDateString()` in about forty places, which
 * means "whatever locale the machine happens to be set to". On a US-configured
 * laptop the same invoice read 06/18/2027 in the grid and 18/06/2027 on the PDF
 * it produced, and 03/04 meant two different days on two screens. Ambiguity in a
 * date is not a cosmetic problem here: it decides which stock ships first and
 * when a supplier gets paid.
 *
 * Timestamps vs calendar dates
 * ----------------------------
 * These are not the same thing and must not be rendered the same way.
 *
 *   - A CALENDAR DATE ("2027-06-18", "18/06/2027") is a day, not an instant. It
 *     is rendered exactly as written. Reading it in local time is what turns an
 *     invoice dated the 18th into the 17th for everyone west of Greenwich.
 *   - A TIMESTAMP (an ISO instant, an epoch, a Date) is a moment, and is
 *     rendered in the reader's own time. Reading it in UTC is what makes a
 *     record created at 9pm in Trinidad show tomorrow's date.
 *
 * The distinction is drawn from the ORIGINAL value, not from the Date object it
 * parses to — by then both look identical.
 *
 * NOTE: a native <input type="date"> renders in the browser's own locale and
 * cannot be made to show dd/mm/yyyy, and en-US shifts typed digits (20/10/2026
 * saved as 01/02/2026). Every typed date box therefore goes through
 * components/DateInput (a TEXT input) and `parseTypedDate` (D109.6): it reads
 * dd/mm/yyyy, REFUSES an impossible date, and shows the parsed date back. A
 * saving form also holds DateInput's validity and refuses to submit while the
 * box holds a refused date. formatDate covers every date the system PRINTS.
 *
 * MIRRORED FILE: src/utils/dateFormat.js is a line-for-line twin.
 * Change one, change both — mirroredUtilParity.test.js fails otherwise.
 */

import { leadsWithDayMonthDate, toInvoiceDate } from "./paymentTerms";

/** "2027-06-18", "18/06/2027", "3.11.26" — a day with no time attached. */
const DATE_ONLY_RE = /^\s*\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}\s*$/;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function readValue(value: unknown): { date: Date; dateOnly: boolean } | null {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { date: value, dateOnly: false };
  }

  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : { date: d, dateOnly: false };
  }

  const s = String(value).trim();
  if (!s) return null;

  // Day-first, via the shared parser: "02/03/2026" is 2 March here, and the
  // native parser is the one thing guaranteed to read it as 3 February. The
  // same holds with a clock after the date. "11/08/2026 09:00" used to reach
  // the native parser below and print as 08/11/2026 (USC-BUG-0009 / 0012 /
  // 0017 / 0025). The house parser reads a calendar day, so that clock is not
  // kept: the right day at 00:00 beats a clock on the wrong month.
  if (DATE_ONLY_RE.test(s) || leadsWithDayMonthDate(s)) {
    const d = toInvoiceDate(s);
    return d ? { date: d, dateOnly: true } : null;
  }

  // An ISO instant, an RFC string, anything else the platform knows. Not
  // toInvoiceDate: that normalises to midnight and would throw the clock away,
  // so every "sent at" would read 00:00.
  const native = new Date(s);
  if (!Number.isNaN(native.getTime())) return { date: native, dateOnly: false };

  const fallback = toInvoiceDate(s);
  return fallback ? { date: fallback, dateOnly: true } : null;
}

function partsOf(value: unknown) {
  const read = readValue(value);
  if (!read) return null;
  const { date, dateOnly } = read;

  return dateOnly
    ? {
        day: date.getUTCDate(),
        month: date.getUTCMonth() + 1,
        year: date.getUTCFullYear(),
        hour: date.getUTCHours(),
        minute: date.getUTCMinutes(),
      }
    : {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        hour: date.getHours(),
        minute: date.getMinutes(),
      };
}

/**
 * dd/mm/yyyy.
 *
 * `fallback` is what to show when there is no date. Each screen has its own
 * convention for an empty cell ('--', '-', '—', ''), so it is passed in rather
 * than imposed.
 */
export function formatDate(value: unknown, fallback = ""): string {
  const p = partsOf(value);
  return p ? `${pad2(p.day)}/${pad2(p.month)}/${p.year}` : fallback;
}

/** dd/mm/yyyy HH:mm, 24-hour — "sent at", "received at", "created at". */
export function formatDateTime(value: unknown, fallback = ""): string {
  const p = partsOf(value);
  return p
    ? `${pad2(p.day)}/${pad2(p.month)}/${p.year} ${pad2(p.hour)}:${pad2(p.minute)}`
    : fallback;
}

/** dd/mm — for a chart axis, where the year is the same on every tick. */
export function formatDayMonth(value: unknown, fallback = ""): string {
  const p = partsOf(value);
  return p ? `${pad2(p.day)}/${pad2(p.month)}` : fallback;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * "8 November 2026" — the month in words, beside the dd/mm/yyyy of a note that
 * asks someone to check a date. A supplier printing month-first prints the same
 * digits the other way round, so "08/11/2026" alone does not say which day USC
 * read (USC-BUG-0055).
 */
export function formatDateSpelled(value: unknown, fallback = ""): string {
  const p = partsOf(value);
  return p ? `${p.day} ${MONTH_NAMES[p.month - 1]} ${p.year}` : fallback;
}

/**
 * The calendar day a STORED date-only value stands for, as yyyy-mm-dd, or null.
 *
 * An invoice date, due date, expiry or delivery date is a day the server keeps as that day's UTC
 * midnight. Its day is therefore its UTC day: read in the browser's own zone, 2026-10-20T00:00:00Z
 * is the evening of the 19th in Trinidad (UTC-4). A yyyy-mm-dd string already is the day and is
 * passed through. Use this for a stored date-only field; a real moment (sent at, created at) goes
 * through formatDateTime instead. The result is what DateInput takes and formatDate prints (D109.6).
 */
export function storedDay(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
  const d = value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

const TYPED_ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const TYPED_DMY_RE = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{4})$/;

/**
 * A date a PERSON typed into a date cell, as yyyy-mm-dd, or null when it is not
 * a real calendar day (D109.6). Day-first: d/m/yyyy, dd/mm/yyyy, dd-mm-yyyy,
 * dd.mm.yyyy (one separator used twice), or an ISO yyyy-mm-dd.
 *
 * Deliberately NOT paymentTerms.parseInvoiceDate: that reader is built for text
 * printed on a supplier invoice, so it swaps to month-first when the second
 * number is above 12 ("10/20/2026" reads as 20 October) and expands two-digit
 * years. Both are guesses, and a typed cell refuses rather than guesses:
 * 2-digit years, 31/02, 13/13 and plain text all return null.
 */
export function parseTypedDate(text: unknown): string | null {
  const s = String(text ?? "").trim();
  let year: number;
  let month: number;
  let day: number;
  const iso = s.match(TYPED_ISO_RE);
  const dmy = s.match(TYPED_DMY_RE);
  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else if (dmy) {
    day = Number(dmy[1]);
    month = Number(dmy[3]);
    year = Number(dmy[4]);
  } else {
    return null;
  }
  if (year < 1000 || month < 1 || month > 12 || day < 1) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  // JS rolls 31/02 into March; a day that moved was never a real day.
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}
